import { z } from "zod";

import { filingSchema } from "./document-filing";
import type { AuditChange } from "./organizations";
import { revolutAmountSchema, type RevolutAmount } from "./revolut";

// Reconciliation: every bank transaction of the current year, kept in the organization's ledger and matched to the
// document behind it, or marked as needing none. The ledger is read from the organization's Revolut connection; only
// the current year's booked transactions are ever kept or shown, the year counted in the organization's own time zone
// (Settings › Identity). The API and the frontend share these.

/**
 * Who resolved a transaction that no person did: the bank sync, which recognizes what needs no document, and the
 * matching that follows it, which matches a transaction to the one document it is sure of.
 */
export const RECONCILIATION = "Reconciliation";

/**
 * Where a transaction of the ledger stands, worked out whenever it is read, never stored:
 *
 * - `needs-document` — nothing resolves it, and no document looks like its own;
 * - `to-confirm` — nothing resolves it yet, but a document looks like its own: a person confirms it, or another;
 * - `matched` — it is matched to the document behind it;
 * - `no-document` — it needs no document: a currency exchange, a transfer between the organization's own accounts, or
 *   whatever a person said needs none, and why.
 *
 * `matched` and `no-document` are the trail complete; the other two are what is left to do.
 */
export const LEDGER_STATUSES = ["needs-document", "to-confirm", "matched", "no-document"] as const;
export type LedgerStatus = (typeof LEDGER_STATUSES)[number];

export const LEDGER_STATUS_LABELS: Readonly<Record<LedgerStatus, string>> = {
	"needs-document": "Needs a document",
	"to-confirm": "To confirm",
	matched: "Matched",
	"no-document": "No document needed",
};

/**
 * Why a transaction needs no document. The bank sync recognizes the first two by itself: money exchanged between two
 * of the organization's own Revolut accounts in different currencies (Revolut's `EXCHANGE`), or sent from one of them
 * to another. A person may give any of them.
 */
export const NO_DOCUMENT_REASONS = ["exchange", "own-transfer", "fee", "tax", "payroll", "other"] as const;
export type NoDocumentReason = (typeof NO_DOCUMENT_REASONS)[number];

export const NO_DOCUMENT_REASON_LABELS: Readonly<Record<NoDocumentReason, string>> = {
	exchange: "Currency exchange",
	"own-transfer": "Transfer between own accounts",
	fee: "Bank fee",
	tax: "Tax",
	payroll: "Payroll",
	other: "Other",
};

/** At most this many characters of a note on why a transaction needs no document. */
export const NO_DOCUMENT_NOTE_MAX_LENGTH = 500;

/**
 * What resolved a transaction, who did and when — `by` is `RECONCILIATION` for what no person did: the document it is
 * matched to, as it was called then; or that it needs no document, why, and what the person who said so noted.
 */
export const ledgerResolutionSchema = z.discriminatedUnion("kind", [
	z.strictObject({
		kind: z.literal("document"),
		fileId: z.string().min(1).max(128),
		fileName: z.string().min(1).max(255),
		by: z.string().min(1).max(80),
		/** When, as an ISO 8601 timestamp in UTC. */
		at: z.iso.datetime(),
	}),
	z.strictObject({
		kind: z.literal("no-document"),
		reason: z.enum(NO_DOCUMENT_REASONS),
		note: z.string().min(1).max(NO_DOCUMENT_NOTE_MAX_LENGTH).optional(),
		by: z.string().min(1).max(80),
		at: z.iso.datetime(),
	}),
]);
export type LedgerResolution = z.infer<typeof ledgerResolutionSchema>;

/**
 * A transaction's id in the ledger: 32 hexadecimal characters, made from its account's id and Revolut's own id for it
 * (`ledgerTransactionId` in the API), since the two legs of an exchange may carry one Revolut id between them.
 */
export const ledgerTransactionIdSchema = z.string().regex(/^[\da-f]{32}$/, "Invalid transaction");

/** The account a transaction was booked on, as it was called when the ledger last read it. */
export const ledgerAccountSchema = z.strictObject({
	id: z.string().min(1).max(40),
	name: z.string().min(1).max(70),
	currency: z.string().regex(/^[A-Z]{3}$/, "Invalid currency"),
});
export type LedgerAccount = z.infer<typeof ledgerAccountSchema>;

/** One booked transaction of the organization's ledger, and where it stands. */
export const ledgerTransactionSchema = z.strictObject({
	id: ledgerTransactionIdSchema,
	account: ledgerAccountSchema,
	/** When the bank booked it, as an ISO 8601 timestamp in UTC. */
	bookedAt: z.iso.datetime(),
	/** The day it was booked in the organization's time zone, which is the year it belongs to. */
	bookedOn: z.iso.date(),
	/** Negative when money left the account. */
	amount: revolutAmountSchema,
	/** What it was, as the bank words it: a merchant, "To Northwind Systems", "Exchanged to EUR". */
	description: z.string().min(1).max(500),
	/** Who was paid, or who paid, when the bank names them. */
	counterparty: z.string().min(1).max(350).optional(),
	/** The reference whoever sent the money gave it, when there is one. */
	reference: z.string().min(1).max(500).optional(),
	status: z.enum(LEDGER_STATUSES),
	/** What resolved it; absent while it needs a document. */
	resolution: ledgerResolutionSchema.optional(),
});
export type LedgerTransaction = z.infer<typeof ledgerTransactionSchema>;

/** How each status is counted over the whole year: every status, zero included. */
const ledgerCountsSchema = z.record(z.enum(LEDGER_STATUSES), z.int().nonnegative());
export type LedgerCounts = z.infer<typeof ledgerCountsSchema>;

/** How the last attempt to read the bank ended: read, or why not. */
export const LEDGER_SYNC_OUTCOMES = ["synced", "not-connected", "reconnect", "failed"] as const;
export type LedgerSyncOutcome = (typeof LEDGER_SYNC_OUTCOMES)[number];

/**
 * What `GET /api/reconciliation` answers: the current year, how many of its transactions the ledger holds and how many
 * of each status, and where reading them from the bank stands.
 */
export const reconciliationSchema = z.strictObject({
	year: z.int().min(2000).max(9999),
	/** Every transaction of the year the ledger holds. */
	total: z.int().nonnegative(),
	counts: ledgerCountsSchema,
	sync: z.strictObject({
		/** When the ledger was last read from the bank; absent until it first is. */
		syncedAt: z.iso.datetime().optional(),
		/**
		 * The first day of the year, in the organization's time zone, from which the ledger holds every booked
		 * transaction: January 1 when the whole year was read, later when the bank would only hand over its last 90
		 * days. Absent until the ledger is first read this year.
		 */
		coveredFrom: z.iso.date().optional(),
		/** When the ledger last tried to read the bank, and how that ended; absent until it first tries. */
		lastAttempt: z.strictObject({ at: z.iso.datetime(), outcome: z.enum(LEDGER_SYNC_OUTCOMES) }).optional(),
	}),
});
export type Reconciliation = z.infer<typeof reconciliationSchema>;

/** The Reconciliation page lists this many transactions a page, the newest first. */
export const LEDGER_PAGE_SIZE = 25;

/** A link to another page of the ledger. The API makes it; a client passes it back unchanged and never reads it. */
export const ledgerPageTokenSchema = z.string().regex(/^[\w-]{1,512}$/, "Invalid page");

/**
 * The query of `GET /api/reconciliation/transactions`: only the transactions of one status when it names one, and the
 * page to show, the newest when there is none.
 */
export const ledgerQuerySchema = z.object({
	status: z.enum(LEDGER_STATUSES).optional(),
	page: ledgerPageTokenSchema.optional(),
});
export type LedgerQuery = z.infer<typeof ledgerQuerySchema>;

/** One page of the current year's ledger, the newest first, with links to the pages beside it: `null` where none. */
export const ledgerPageSchema = z.strictObject({
	year: z.int().min(2000).max(9999),
	transactions: z.array(ledgerTransactionSchema).max(LEDGER_PAGE_SIZE),
	/** How many transactions of the status asked for, or of every status, the year holds, every page counted. */
	total: z.int().nonnegative(),
	/** How many of each status the year holds, whatever the page lists: what the page's filters show. */
	counts: ledgerCountsSchema,
	next: ledgerPageTokenSchema.nullable(),
	previous: ledgerPageTokenSchema.nullable(),
});
export type LedgerPage = z.infer<typeof ledgerPageSchema>;

/** What `POST /api/reconciliation/sync` answers: when the bank was asked, which the ledger's next attempt follows. */
export const ledgerSyncRequestedSchema = z.strictObject({ requestedAt: z.iso.datetime() });
export type LedgerSyncRequested = z.infer<typeof ledgerSyncRequestedSchema>;

/**
 * Why a document looks like a transaction's own, each a way the two agree: the same amount in the same currency, paid
 * the way the document's type is paid; a day of booking that fits the document's dates; the same party; and the
 * document's number in what the bank says of the transaction. A document is suggested only when the amount agrees and
 * something else does too.
 */
export const LEDGER_MATCH_SIGNALS = ["amount", "dates", "names", "number"] as const;
export type LedgerMatchSignal = (typeof LEDGER_MATCH_SIGNALS)[number];

/** An Intake document as reconciliation offers it for a transaction, with what Document Filing read from it, if anything. */
export const ledgerDocumentSchema = z.strictObject({
	fileId: z.string().min(1).max(128),
	fileName: z.string().min(1).max(255),
	/** When it was uploaded, as an ISO 8601 timestamp in UTC. */
	uploadedAt: z.iso.datetime(),
	filing: filingSchema.optional(),
	/** How many transactions it is matched to already, in any year. */
	payments: z.int().nonnegative(),
});
export type LedgerDocument = z.infer<typeof ledgerDocumentSchema>;

/** A document that looks like a transaction's own, and every way the two agree. */
export const ledgerSuggestionSchema = ledgerDocumentSchema.extend({
	agrees: z.array(z.enum(LEDGER_MATCH_SIGNALS)).min(2),
});
export type LedgerSuggestion = z.infer<typeof ledgerSuggestionSchema>;

/** At most this many documents are suggested for one transaction, the likeliest first. */
export const MAX_LEDGER_SUGGESTIONS = 5;

/**
 * What `GET /api/reconciliation/transactions/{transactionId}` answers: one transaction of the current year's ledger and,
 * while nothing resolves it, the documents that look like its own, the likeliest first.
 */
export const ledgerTransactionDetailSchema = z.strictObject({
	year: z.int().min(2000).max(9999),
	transaction: ledgerTransactionSchema,
	suggestions: z.array(ledgerSuggestionSchema).max(MAX_LEDGER_SUGGESTIONS),
});
export type LedgerTransactionDetail = z.infer<typeof ledgerTransactionDetailSchema>;

/** At most this many documents answer one search of them. */
export const MAX_LEDGER_DOCUMENTS = 20;

/**
 * The query of `GET /api/reconciliation/documents`: the organization's Intake documents whose name, or what Document
 * Filing read of whom they are with or of their number, holds `q` — every one when there is none — newest first.
 */
export const ledgerDocumentsQuerySchema = z.object({
	q: z.string().trim().max(100).optional(),
});
export type LedgerDocumentsQuery = z.infer<typeof ledgerDocumentsQuerySchema>;

/** What a search of the documents answers: the newest `MAX_LEDGER_DOCUMENTS` that match it. */
export const ledgerDocumentsSchema = z.strictObject({
	documents: z.array(ledgerDocumentSchema).max(MAX_LEDGER_DOCUMENTS),
});
export type LedgerDocuments = z.infer<typeof ledgerDocumentsSchema>;

/**
 * What `PUT /api/reconciliation/transactions/{transactionId}/resolution` takes: the document a transaction is matched
 * to, or that it needs no document, why, and a note when there is more to say.
 */
export const ledgerResolutionRequestSchema = z.discriminatedUnion("kind", [
	z.strictObject({ kind: z.literal("document"), fileId: z.string().trim().min(1).max(128) }),
	z.strictObject({
		kind: z.literal("no-document"),
		reason: z.enum(NO_DOCUMENT_REASONS),
		note: z.string().trim().max(NO_DOCUMENT_NOTE_MAX_LENGTH).optional(),
	}),
]);
export type LedgerResolutionRequest = z.infer<typeof ledgerResolutionRequestSchema>;

/** What the routes that resolve a transaction, or reopen one, answer: the transaction as it now stands. */
export const ledgerTransactionAnswerSchema = z.strictObject({ transaction: ledgerTransactionSchema });
export type LedgerTransactionAnswer = z.infer<typeof ledgerTransactionAnswerSchema>;

/** One transaction a document is matched to, as the document's page lists it. */
export const documentPaymentSchema = z.strictObject({
	transactionId: ledgerTransactionIdSchema,
	/** The year of the ledger the transaction is in. */
	year: z.int().min(2000).max(9999),
	account: ledgerAccountSchema,
	bookedAt: z.iso.datetime(),
	/** The day it was booked in the organization's time zone. */
	bookedOn: z.iso.date(),
	amount: revolutAmountSchema,
	description: z.string().min(1).max(500),
	counterparty: z.string().min(1).max(350).optional(),
	/** Who matched it — `RECONCILIATION` when no person did — and when. */
	matchedBy: z.string().min(1).max(80),
	matchedAt: z.iso.datetime(),
});
export type DocumentPayment = z.infer<typeof documentPaymentSchema>;

/**
 * What `GET /api/reconciliation/documents/{fileId}/payments` answers: every transaction one document is matched to, in
 * any year, the latest booked first.
 */
export const documentPaymentsSchema = z.strictObject({ payments: z.array(documentPaymentSchema) });
export type DocumentPayments = z.infer<typeof documentPaymentsSchema>;

/** What an entry of the audit log's `finance` category says. */
export interface FinanceAuditEvent {
	readonly action: string;
	readonly detail: string;
}

/** What an audit entry names a transaction by: its amount as the bank gave it, and what the bank says it was. */
interface AuditedTransaction {
	readonly amount: RevolutAmount;
	readonly description: string;
}

function transactionNamed({ amount, description }: AuditedTransaction): string {
	return `${amount.amount} ${amount.currency} · ${description}`;
}

/** "Transaction matched" / "-1850.00 EUR · To Protokol d.o.o. · invoice-protokol-0912.pdf". */
export function transactionMatchedAudit(transaction: AuditedTransaction, fileName: string): FinanceAuditEvent {
	return { action: "Transaction matched", detail: `${transactionNamed(transaction)} · ${fileName}` };
}

/** "Transaction needs no document" / "-4.00 EUR · Monthly plan fee · Bank fee"; a note's contents are never among it. */
export function transactionNeedsNoDocumentAudit(
	transaction: AuditedTransaction,
	reason: NoDocumentReason,
): FinanceAuditEvent {
	return {
		action: "Transaction needs no document",
		detail: `${transactionNamed(transaction)} · ${NO_DOCUMENT_REASON_LABELS[reason]}`,
	};
}

/** Why the ledger was read from the bank, as the audit entry of the read says it. */
export const LEDGER_UPDATE_REASON_LABELS = {
	connected: "Revolut was connected",
	daily: "The daily update",
	asked: "A member asked",
} as const satisfies Record<string, string>;

/** Why an attempt to read the ledger from the bank kept nothing, as its audit entry says it. */
const LEDGER_NOT_UPDATED: Readonly<Record<Exclude<LedgerSyncOutcome, "synced">, string>> = {
	"not-connected": "not connected",
	reconnect: "needs reconnecting",
	failed: "the read failed",
};

/**
 * The audit entry of one read of the ledger from the bank, under `RECONCILIATION`: "Ledger updated" / "Revolut · 48
 * read, 3 new", or "Ledger not updated" / "Revolut · needs reconnecting" — with why it was read, and how many
 * transactions it read and kept, as values.
 */
export function ledgerUpdatedAudit(
	outcome: LedgerSyncOutcome,
	reason: keyof typeof LEDGER_UPDATE_REASON_LABELS,
	{ read, added }: { readonly read: number; readonly added: number },
): FinanceAuditEvent & { changes: AuditChange[] } {
	const why = { label: "Why", value: LEDGER_UPDATE_REASON_LABELS[reason] };
	if (outcome !== "synced") {
		return { action: "Ledger not updated", detail: `Revolut · ${LEDGER_NOT_UPDATED[outcome]}`, changes: [why] };
	}
	const kept = added === 0 ? "none new" : `${String(added)} new`;
	return {
		action: "Ledger updated",
		detail: read === 0 ? "Revolut · nothing new" : `Revolut · ${String(read)} read, ${kept}`,
		changes: [why, { label: "New transactions", value: String(added) }, { label: "Read", value: String(read) }],
	};
}

/** "Requested a ledger update" / "Revolut": a member asked for the ledger to be read from the bank now. */
export function ledgerUpdateRequestedAudit(): FinanceAuditEvent {
	return { action: "Requested a ledger update", detail: "Revolut" };
}

/** "Transaction reopened" / "-1850.00 EUR · To Protokol d.o.o.": what resolved it is taken back. */
export function transactionReopenedAudit(transaction: AuditedTransaction): FinanceAuditEvent {
	return { action: "Transaction reopened", detail: transactionNamed(transaction) };
}
